import { alpha, Button, Stack, Typography, useTheme } from "@mui/material";
import { Sparkles } from "lucide-react";
import { type FC, useMemo } from "react";
import { Trans } from "react-i18next";
import {
	monthlyContribution,
	monthsUntil,
} from "web_myplann/Goals/Helpers/monthlyContribution";
import {
	getResourcePath,
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { LoadingButton } from "web_myplann/Shared/Components/LoadingButton";
import { NATIVE_BOTTOM_BAR_SPACING } from "web_myplann/Shared/constants";

type Props = {
	savingsTarget: number;
	dueDate: Date | undefined | null;
	submitLabel: string;
	cancelLabel: string;
	submitDisabled: boolean;
	isSubmitting: boolean;
	onCancel: () => void;
};

export const MonthlySavingsHero: FC<Props> = ({
	savingsTarget,
	dueDate,
	submitLabel,
	cancelLabel,
	submitDisabled,
	isSubmitting,
	onCancel,
}) => {
	const theme = useTheme();
	const { t } = useResource();

	const { monthly, monthlyDelayed, extraMonthly } = useMemo(() => {
		const months = monthsUntil(dueDate);
		const monthly = monthlyContribution(savingsTarget, dueDate);

		let monthlyDelayed = 0;
		const delayedMonths = months - 12;
		if (delayedMonths >= 6) {
			const r = 0.04 / 12;
			const factor = (1 + r) ** delayedMonths - 1;
			monthlyDelayed = Math.round((savingsTarget * r) / factor);
		}
		const extraMonthly =
			monthlyDelayed > monthly ? monthlyDelayed - monthly : 0;

		return { monthly, monthlyDelayed, extraMonthly };
	}, [savingsTarget, dueDate]);

	return (
		<Stack
			sx={{
				flex: "1 0 auto",
				mt: 1.2,
				background: theme.gradients.primaryHero,
				px: 2,
				pt: 2,
				pb: NATIVE_BOTTOM_BAR_SPACING,
			}}
		>
			<Stack
				direction="row"
				alignItems="center"
				gap={0.6}
				sx={{
					alignSelf: "flex-start",
					px: 1,
					py: 0.4,
					mb: 1.2,
					borderRadius: 999,
					bgcolor: alpha(theme.palette.common.white, 0.7),
				}}
			>
				<Sparkles size={12} color={theme.palette.primary.main} />
				<Typography
					variant="caption"
					color={theme.palette.grey[700]}
					sx={{
						fontWeight: 600,
						textTransform: "uppercase",
						letterSpacing: "0.05em",
					}}
				>
					{t(Resources.Goals.Form.MonthlyConversion.kicker)}
				</Typography>
			</Stack>

			<Stack direction="row" alignItems="baseline" gap={0.6}>
				<Typography
					variant="h1"
					tabular
					sx={{
						lineHeight: 1,
						flexShrink: 0,
					}}
				>
					{monthly.toLocaleString("cs-CZ")}
				</Typography>
				<Typography
					variant="caption"
					color={theme.palette.grey[600]}
					sx={{ whiteSpace: "nowrap" }}
				>
					{t(Resources.Goals.Form.MonthlyConversion.heroSuffix)}
				</Typography>
			</Stack>

			{extraMonthly > 0 && (
				<Typography
					variant="caption"
					color={theme.palette.grey[600]}
					tabular
					sx={{ mt: 1.2, lineHeight: 1.375 }}
				>
					<Trans
						i18nKey={getResourcePath(
							Resources.Goals.Form.MonthlyConversion.fomo,
						)}
						values={{
							monthlyDelayed: monthlyDelayed.toLocaleString("cs-CZ"),
							extra: extraMonthly.toLocaleString("cs-CZ"),
						}}
						components={{
							bold: (
								<Typography
									component="span"
									variant="caption"
									color={theme.palette.grey[900]}
									tabular
									sx={{ fontWeight: 600 }}
								/>
							),
						}}
					/>
				</Typography>
			)}

			<Typography
				variant="caption"
				color={theme.palette.grey[600]}
				textAlign="center"
				sx={{
					mt: 1.6,
					pt: 1.6,
					borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.4)}`,
					lineHeight: 1.375,
				}}
			>
				{t(Resources.Goals.Form.MonthlyConversion.caption)}
			</Typography>

			<Stack mt={0.8} gap={0.4}>
				<LoadingButton
					type="submit"
					variant="primary"
					disabled={submitDisabled}
					loading={isSubmitting}
				>
					{submitLabel}
				</LoadingButton>
				<Button
					variant="textLink"
					color="neutral"
					onClick={onCancel}
					sx={{ width: "100%", justifyContent: "center", py: 0.8 }}
				>
					{cancelLabel}
				</Button>
			</Stack>
		</Stack>
	);
};
